import { publicEnv } from "../config/publicEnv";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${publicEnv.apiBaseUrl}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiRequestError(
      "Het lukt op dit moment niet om verbinding te maken. Je ingevulde gegevens blijven staan. Probeer het over een moment opnieuw.",
      0,
      "network_error",
    );
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // lege of niet-JSON respons, laat body op null
  }

  if (!response.ok) {
    const errorBody = body as {
      message?: string;
      code?: string;
      fieldErrors?: Record<string, string>;
    } | null;
    throw new ApiRequestError(
      errorBody?.message ?? "Er ging iets mis. Probeer het opnieuw.",
      response.status,
      errorBody?.code,
      errorBody?.fieldErrors,
    );
  }

  return body as T;
}

export interface AvailabilityResponse {
  meta: {
    minNoticeDays: number;
    maxBookingMonths: number;
    timezone: string;
    earliestDate: string;
    latestDate: string;
  };
  days: { date: string; available: boolean; reason?: "unavailable" }[];
}

export function fetchAvailability(from: string, to: string): Promise<AvailabilityResponse> {
  return request<AvailabilityResponse>(`/api/availability?from=${from}&to=${to}`);
}

export interface BookingSubmissionPayload {
  eventDate: string;
  startTime: string;
  endTime: string;
  endNextDay: boolean;
  fullName: string;
  email: string;
  phone?: string;
  location: string;
  eventType: string;
  eventTypeOther?: string;
  guestCount?: string;
  customPrintDesign: string;
  comments?: string;
  turnstileToken: string;
  honeypot: string;
  formRenderedAtMs: number;
  idempotencyKey: string;
}

export interface BookingSubmissionResult {
  publicReference: string;
  status: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  endNextDay: boolean;
  location: string;
  eventTypeLabel: string;
  statusToken: string;
}

export function submitBookingRequest(
  payload: BookingSubmissionPayload,
): Promise<BookingSubmissionResult> {
  return request<BookingSubmissionResult>("/api/requests", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
